import { useMemo } from "react";
import useSWR, { mutate } from "swr";

import * as api from "../api";
import { useAttempts } from "./useAttempts";
import { useCompetitionEntries } from "./useCompetition";

export const useRouteQueueUserUUIDs = ({
  routeUUID,
  competitionUUID,
}: {
  routeUUID?: string;
  competitionUUID?: string;
}) => {
  const { data, error, isValidating } = useSWR<string[]>(
    `/competitions/${competitionUUID}/route-queues/${routeUUID}`
  );
  return { queue: data, error, isValidating };
};

export const useRouteQueueEntries = ({
  routeUUID,
  competitionUUID,
}: {
  routeUUID?: string;
  competitionUUID?: string;
}) => {
  const { entries } = useCompetitionEntries(competitionUUID);
  const { attemptsByUser } = useAttempts(routeUUID);
  const {
    queue: queueUserUUIDs,
    isValidating,
    error,
  } = useRouteQueueUserUUIDs({
    routeUUID,
    competitionUUID,
  });

  const queue = useMemo(() => {
    if (!entries || !queueUserUUIDs) {
      return undefined;
    }
    return queueUserUUIDs
      .map((userUUID) => {
        const entry = entries.find((x) => x.user.uuid === userUUID);
        if (!entry) {
          return undefined;
        }
        return {
          ...entry,
          attempts: attemptsByUser && attemptsByUser[userUUID],
        };
      })
      .filter((item): item is Exclude<typeof item, undefined> => !!item);
  }, [entries, queueUserUUIDs, attemptsByUser]);
  return { queue, isValidating, error };
};

export const addToQueue = async ({
  routeUUID,
  competitionUUID,
  userUUID,
}: {
  routeUUID: string;
  competitionUUID: string;
  userUUID: string;
}) => {
  const newQueue = await api.addToQueue({
    routeUUID,
    competitionUUID,
    userUUID,
  });
  mutate(
    `/competitions/${competitionUUID}/route-queues/${routeUUID}`,
    newQueue
  );
};

export const putQueue = ({
  routeUUID,
  competitionUUID,
  queue,
}: {
  routeUUID: string;
  competitionUUID: string;
  queue: string[];
}) => {
  mutate(
    `/competitions/${competitionUUID}/route-queues/${routeUUID}`,
    api.putQueue({ routeUUID, competitionUUID, queue }),
    {
      optimisticData: queue,
      revalidate: false,
    }
  );
};
